import { useState } from 'react';
import { AlertTriangle, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { UI_STRINGS } from './locale';

interface SoftDeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Режим: soft (в корзину) или permanent (навсегда) */
  mode: 'soft' | 'permanent';
  /** Название сущности (единственное число) */
  entityLabel: string;
  /** Имя конкретной записи (для подтверждения в permanent режиме) */
  entityName?: string;
  /** Колбэк подтверждения */
  onConfirm: () => void | Promise<void>;
  /** Состояние загрузки */
  isPending?: boolean;
}

/**
 * Диалог подтверждения удаления.
 *
 * Режим `soft` — простое подтверждение (перемещение в архив/корзину).
 * Режим `permanent` — требует ввода имени записи для подтверждения безвозвратного удаления.
 */
export function SoftDeleteDialog({
  open,
  onOpenChange,
  mode,
  entityLabel,
  entityName,
  onConfirm,
  isPending = false,
}: SoftDeleteDialogProps) {
  const [confirmText, setConfirmText] = useState('');

  const isPermanent = mode === 'permanent';
  const canConfirm = !isPermanent || confirmText === entityName;

  const handleConfirm = async () => {
    await onConfirm();
    setConfirmText('');
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setConfirmText('');
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div
              className={cn(
                'h-10 w-10 rounded-full flex items-center justify-center shrink-0',
                isPermanent
                  ? 'bg-destructive/10 text-destructive'
                  : 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
              )}
            >
              {isPermanent ? (
                <Trash2 className="h-5 w-5" />
              ) : (
                <AlertTriangle className="h-5 w-5" />
              )}
            </div>
            <DialogTitle>
              {isPermanent ? UI_STRINGS.deleteForever(entityLabel) : UI_STRINGS.archiveEntity(entityLabel)}
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {isPermanent ? (
            <>
              <p className="text-sm text-muted-foreground">
                {UI_STRINGS.permanentWarningPrefix}
                {' '}
                <span className="font-semibold text-destructive">{UI_STRINGS.permanentWarning}</span>
                .
                {' '}
                {UI_STRINGS.permanentDescription(entityLabel)}
              </p>
              {entityName && (
                <div className="space-y-2">
                  <Label htmlFor="confirm-name" className="text-sm">
                    {UI_STRINGS.confirmNamePrompt}
                    {' '}
                    <span className="font-mono font-semibold text-foreground bg-muted px-1.5 py-0.5 rounded">
                      {entityName}
                    </span>
                  </Label>
                  <Input
                    id="confirm-name"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder={entityName}
                    autoComplete="off"
                    className={cn(
                      confirmText && confirmText !== entityName && 'border-destructive',
                    )}
                  />
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              {UI_STRINGS.archiveDescription(entityLabel)}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isPending}>
            {UI_STRINGS.cancel}
          </Button>
          <Button
            variant="destructive"
            onClick={() => { void handleConfirm(); }}
            disabled={!canConfirm || isPending}
          >
            {isPending
              ? UI_STRINGS.deleting
              : (isPermanent
                ? UI_STRINGS.deletePermanently
                : UI_STRINGS.archive)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
